import type { IChannel } from "containers/Channels/types";
import { getCleanStartorEndDay, isInfinity, replaceComma } from "helpers/utils";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";
import type { ChannelCompensationGroup } from "store/channelStore";
import type { TPeriod } from "store/periodStore";
import type { Binary } from "types";

import type { ChannelData, ChannelParams } from ".";
import { resolveCalculationBaseColumn } from "./ChannelsStep/calculationBaseColumnUtils";

const groupFormulaKey = (groupId: number, formatTypeId?: number | null) => `${groupId}:${formatTypeId ?? 0}`;

const compensationGroupFormulaSignature = (row: ChannelParams) =>
    JSON.stringify({
        formatTypeId: row.formatTypeId ?? null,
        data: row.data,
        calculationBaseColumn: row.calculationBaseColumn,
        milestoneBaseColumn: row.milestoneBaseColumn,
        showDefaultConfiguration: row.formatTypeId == null ? row.showDefaultConfiguration === false : undefined,
    });

/** True when every channel in the group has the same default + override formulas (ladders and bases). */
export const areCompensationGroupFormulasSynced = (groupRows: ChannelParams[]): boolean => {
    const rowsByChannelId = new Map<number, ChannelParams[]>();
    for (const row of groupRows) {
        const rows = rowsByChannelId.get(row.channelId) ?? [];
        rows.push(row);
        rowsByChannelId.set(row.channelId, rows);
    }
    if (rowsByChannelId.size <= 1) return true;

    const signatures = [...rowsByChannelId.values()].map((rows) =>
        rows.map(compensationGroupFormulaSignature).sort().join("|"),
    );
    return signatures.every((signature) => signature === signatures[0]);
};

/** Copies the reference channel's default + override formulas onto every other channel in the group. */
export const alignCompensationGroupFormulasOnSync = (
    chosenChannels: ChannelParams[],
    groupChannelIds: number[],
    referenceChannelId: number,
    groupId: number,
): ChannelParams[] => {
    const groupChannelIdSet = new Set(groupChannelIds);
    const rowsOutsideGroup = chosenChannels.filter((row) => !groupChannelIdSet.has(row.channelId));
    const referenceRows = chosenChannels.filter((row) => row.channelId === referenceChannelId);
    if (referenceRows.length === 0) return chosenChannels;

    const alignedGroupRows = groupChannelIds.flatMap((channelId) =>
        referenceRows.map((row) => ({
            ...row,
            channelId,
            groupId,
        })),
    );
    return [...rowsOutsideGroup, ...alignedGroupRows];
};

/** Absent or true: the contract form shows the channel default formula. */
export const showsDefaultChannelConfiguration = (channel: { showDefaultConfiguration?: boolean } | undefined) =>
    channel?.showDefaultConfiguration !== false;

/**
 * Reads the hidden-default marker from a typology channel or from term milestones,
 * and returns milestones without that display flag.
 */
export const splitDefaultConfigurationFlag = (
    milestones: ChannelData[],
    channelFlag?: boolean,
): { milestones: ChannelData[]; showDefaultConfiguration: false | undefined } => {
    const hidden =
        channelFlag === false || milestones.some((milestone) => milestone.showDefaultConfiguration === false);

    return {
        showDefaultConfiguration: hidden ? false : undefined,
        milestones: milestones.map(({ showDefaultConfiguration: _displayFlag, ...milestone }) => milestone),
    };
};

const hiddenDefaultMilestone = (): ChannelData => ({ percentage: 0, start: 0, quantity: Infinity });

type FormatChannelsForSavingOptions = {
    /** Typology JSON keeps the display flag. A term payload omits it. */
    keepShowDefaultConfiguration?: boolean;
};

const belongsToCompensationGroup = (channel: ChannelParams): channel is ChannelParams & { groupId: number } =>
    channel.groupId != null && channel.groupId >= 0;

/** Milestone column of the channel that still has tiers, per group formula. */
const groupMilestoneColumnFromChannelsWithTiers = (
    chosenChannels: ChannelParams[],
    channelById: Map<number, IChannel>,
) => {
    const columns = new Map<string, CalculationBaseColumn>();
    for (const channel of chosenChannels) {
        if (!belongsToCompensationGroup(channel)) continue;
        const companyChannel = channelById.get(channel.channelId);
        if (!companyChannel?.hasMilestones) continue;
        const key = groupFormulaKey(channel.groupId, channel.formatTypeId);
        if (columns.has(key)) continue;
        const calculationBaseColumn = resolveCalculationBaseColumn(companyChannel, channel.calculationBaseColumn);
        columns.set(key, channel.milestoneBaseColumn ?? calculationBaseColumn);
    }
    return columns;
};

const milestoneColumnIsValidForCalculationBase = (
    calculationBaseColumn: CalculationBaseColumn,
    milestoneBaseColumn: CalculationBaseColumn,
) => milestoneBaseColumn === "quantity" || milestoneBaseColumn === calculationBaseColumn;

/**
 * Every channel of a formula uses the milestone column of the channel that still has tiers,
 * whatever the list order. A channel without tiers and without that column follows its calculation base.
 */
const milestoneColumnForSaving = (
    companyChannel: IChannel | undefined,
    calculationBaseColumn: CalculationBaseColumn,
    groupMilestoneColumn: CalculationBaseColumn | undefined,
    sharedMilestoneColumn: CalculationBaseColumn | undefined,
    channelMilestoneColumn: CalculationBaseColumn | undefined,
): CalculationBaseColumn => {
    if (
        groupMilestoneColumn != null &&
        milestoneColumnIsValidForCalculationBase(calculationBaseColumn, groupMilestoneColumn)
    ) {
        return groupMilestoneColumn;
    }
    const channelHasNoTiers = !!companyChannel && !companyChannel.hasMilestones;
    if (channelHasNoTiers) return calculationBaseColumn;
    return sharedMilestoneColumn ?? channelMilestoneColumn ?? calculationBaseColumn;
};

/**
 * Used in term forms (either basic or template) for formatting channels data before submitting form.
 * A hidden default formula is stored at 0% so the contract still has a default row.
 * @param chosenChannels Channels chosen within form and their data.
 * @param channels Channels of the company
 */
export const formatChannelsForSaving = (
    chosenChannels: ChannelParams[],
    channels: IChannel[],
    { keepShowDefaultConfiguration = false }: FormatChannelsForSavingOptions = {},
) => {
    const channelById = new Map(channels.map((channel) => [channel.id, channel]));
    const groupMilestoneColumns = groupMilestoneColumnFromChannelsWithTiers(chosenChannels, channelById);
    // Compensation groups share one calculation/milestone base per formula (default or format override).
    const groupBasesByFormula = new Map<
        string,
        { calculationBaseColumn: CalculationBaseColumn; milestoneBaseColumn: CalculationBaseColumn }
    >();
    for (const channel of chosenChannels) {
        if (!belongsToCompensationGroup(channel)) continue;
        const key = groupFormulaKey(channel.groupId, channel.formatTypeId);
        if (groupBasesByFormula.has(key)) continue;
        const currentChannel = channelById.get(channel.channelId);
        const calculationBaseColumn = currentChannel
            ? resolveCalculationBaseColumn(currentChannel, channel.calculationBaseColumn)
            : channel.calculationBaseColumn ?? ("quantity" as CalculationBaseColumn);
        groupBasesByFormula.set(key, {
            calculationBaseColumn,
            milestoneBaseColumn: channel.milestoneBaseColumn ?? calculationBaseColumn,
        });
    }

    return chosenChannels.map((channel: ChannelParams) => {
        const currentChannel = channelById.get(channel.channelId);
        const groupBases = belongsToCompensationGroup(channel)
            ? groupBasesByFormula.get(groupFormulaKey(channel.groupId, channel.formatTypeId))
            : undefined;
        // Base and milestone columns are contract-scoped: milestone defaults to the base column.
        const calculationBaseColumn =
            groupBases?.calculationBaseColumn ??
            (currentChannel
                ? resolveCalculationBaseColumn(currentChannel, channel.calculationBaseColumn)
                : channel.calculationBaseColumn ?? ("quantity" as CalculationBaseColumn));
        const groupMilestoneColumn = belongsToCompensationGroup(channel)
            ? groupMilestoneColumns.get(groupFormulaKey(channel.groupId, channel.formatTypeId))
            : undefined;
        const milestoneBaseColumn = milestoneColumnForSaving(
            currentChannel,
            calculationBaseColumn,
            groupMilestoneColumn,
            groupBases?.milestoneBaseColumn,
            channel.milestoneBaseColumn,
        );
        // Milestone thresholds are stored in the milestone column unit: monetary columns are in cents.
        const channelTypeMultiplier = milestoneBaseColumn === "quantity" ? 1 : 100;

        const hideDefaultFormula = channel.formatTypeId == null && channel.showDefaultConfiguration === false;
        const milestones = hideDefaultFormula ? [hiddenDefaultMilestone()] : (channel.data as ChannelData[]);
        const newData = milestones.map((milestone) => {
            const { showDefaultConfiguration: _displayFlag, ...milestoneFields } = milestone;
            return {
                ...milestoneFields,
                percentage: milestone.percentage === "" ? 0 : milestone.percentage,
                quantity: isInfinity(milestone.quantity) ? "Infinity" : +milestone.quantity * channelTypeMultiplier,
                // Term rows carry the marker inside `data`. The typology keeps it on the channel instead.
                ...(hideDefaultFormula && !keepShowDefaultConfiguration ? { showDefaultConfiguration: false } : {}),
            };
        });

        return {
            channelId: channel.channelId,
            data: JSON.stringify(newData),
            calculationBaseColumn,
            milestoneBaseColumn,
            formatTypeId: channel.formatTypeId ?? null,
            ...(keepShowDefaultConfiguration && hideDefaultFormula ? { showDefaultConfiguration: false as const } : {}),
        };
    });
};

/**
 * Used when term/template created and we're showing details or wanting to edit.
 */
export const getGroupWithChannels = (
    channelCompensationGroups: ChannelCompensationGroup[],
    chosenChannels: ChannelParams[] | undefined,
) => {
    const allGroups = channelCompensationGroups.map((group) => {
        const groupRows = chosenChannels?.filter((channel) => channel.groupId === group.id) ?? [];
        const defaultRows = groupRows.filter((channel) => channel.formatTypeId == null);

        return {
            ...group,
            channels: defaultRows,
            data: defaultRows.length > 0 ? (defaultRows[0].data as ChannelData[]) : [],
            synced: areCompensationGroupFormulasSynced(groupRows),
        };
    });

    return allGroups.filter((group) => group.channels.length > 0);
};

/**
 * Checks if the element (title or beneficiary) has active terms on other period than current one (for multiperiods companies).
 * @returns boolean
 */
export const checkElementTerms = ({
    terms,
    currentPeriodTypeId,
    activePeriods,
}: {
    terms: any;
    currentPeriodTypeId: number;
    activePeriods: TPeriod[];
}) => {
    const activePeriodIds = activePeriods.map((period) => period.periodTypeId);
    // Get terms that exists on activePeriods:
    const termsOnActivePeriod = terms.filter((item) => activePeriodIds.includes(item.periodTypeId));

    // Check if active terms exist on other period than current one.
    const termInOtherPeriod = termsOnActivePeriod.filter((item) => item.periodTypeId !== currentPeriodTypeId);
    return termInOtherPeriod;
};

/**
 * Deduces default radio answer to "published before this period?".
 * Published before period start → 1 (Yes), on or after period start → 0 (No).
 */
/** Minimum advance slice payment date when the title was not published before the contract period. */
export const getTermFormMinAdvancePaymentDate = (
    isTitlePublishedBeforePeriod: Binary | number,
    currentPeriod: TPeriod | undefined,
): Date | undefined => {
    if (Number(isTitlePublishedBeforePeriod) === 1 || !currentPeriod?.periodStart) {
        return undefined;
    }

    return getCleanStartorEndDay(currentPeriod.periodStart, { time: "start" });
};

export const getDefaultAdvanceSlicePaymentDate = (minPaymentDate?: Date): Date => {
    const today = getCleanStartorEndDay(new Date(), { time: "start" });
    if (!minPaymentDate) return today;
    return today < minPaymentDate ? minPaymentDate : today;
};

export const clampAdvanceSlicePaymentDate = (
    paymentDate: Date | string | null | undefined,
    minPaymentDate?: Date,
): Date => {
    const date = paymentDate
        ? getCleanStartorEndDay(paymentDate as Date | string, { time: "start" })
        : getDefaultAdvanceSlicePaymentDate(minPaymentDate);

    if (!minPaymentDate || date >= minPaymentDate) return date;
    return minPaymentDate;
};

/**
 * Non-zero opening balance implies the title was published before this period.
 */
export const shouldSetPublishedBeforePeriodFromOpeningBalance = (
    openingBalance: string | number | null | undefined,
): boolean => {
    if (openingBalance === "" || openingBalance == null) {
        return false;
    }

    return +replaceComma(String(openingBalance)) !== 0;
};

/** Fields hidden when "published before this period" is No — cleared on create only. */
export const TITLE_PUBLISHED_BEFORE_PERIOD_FIELD_NAMES = [
    "advancePayment",
    "initialProvision",
    "initialAdvance",
    "cumulativeRights",
] as const;

export type TitlePublishedBeforePeriodFieldName = (typeof TITLE_PUBLISHED_BEFORE_PERIOD_FIELD_NAMES)[number];

export const shouldClearTitlePublishedBeforePeriodFields = (
    mode: "edit" | "new",
    isTitlePublishedBeforePeriod: Binary | number | string,
): boolean => mode === "new" && Number(isTitlePublishedBeforePeriod) === 0;

export const clearTitlePublishedBeforePeriodFields = (
    setValue: (name: TitlePublishedBeforePeriodFieldName, value: string) => void,
    clearErrors?: (names: readonly TitlePublishedBeforePeriodFieldName[]) => void,
): void => {
    for (const fieldName of TITLE_PUBLISHED_BEFORE_PERIOD_FIELD_NAMES) {
        setValue(fieldName, "");
    }

    clearErrors?.(TITLE_PUBLISHED_BEFORE_PERIOD_FIELD_NAMES);
};

export type FormatTypeOption = { id: number; name: string };

/** Named types first; imported overrides without a label keep a `#id` fallback. */
export const formatTypesForOverridePanel = (
    formatTypesOnWork: FormatTypeOption[],
    overrideIds: Iterable<number>,
): { remaining: FormatTypeOption[]; active: FormatTypeOption[] } => {
    const overrideIdSet = new Set(overrideIds);
    const remaining: FormatTypeOption[] = [];
    const namedActive: FormatTypeOption[] = [];
    const namedActiveIds = new Set<number>();

    for (const formatType of formatTypesOnWork) {
        if (overrideIdSet.has(formatType.id)) {
            namedActive.push(formatType);
            namedActiveIds.add(formatType.id);
            continue;
        }
        remaining.push(formatType);
    }

    const unnamedActive = [...overrideIdSet]
        .filter((id) => !namedActiveIds.has(id))
        .map((id) => ({ id, name: `#${id}` }));

    return { remaining, active: [...namedActive, ...unnamedActive] };
};

export const getEarliestWorkReleaseDate = (
    work: { titles?: Array<{ releaseDate?: string | Date | null }> } | null | undefined,
): string | Date | null => {
    const dates = (work?.titles ?? [])
        .map((title) => title.releaseDate)
        .filter((date): date is string | Date => date != null && `${date}`.length > 0);
    if (dates.length === 0) {
        return null;
    }
    return [...dates].sort((left, right) => new Date(left).getTime() - new Date(right).getTime())[0];
};

export const getDefaultIsTitlePublishedBeforePeriod = (
    releaseDate: string | Date | null | undefined,
    period: TPeriod | undefined,
): Binary => {
    try {
        if (!releaseDate || !period?.periodStart) {
            return 0;
        }

        const _releaseDate = getCleanStartorEndDay(releaseDate, { time: "start" });
        const periodStart = getCleanStartorEndDay(period.periodStart, { time: "start" });

        return _releaseDate < periodStart ? 1 : 0;
    } catch (err) {
        return 0;
    }
};

export const isThereNegativeBalance = ({ chosenAdvance, chosenInitialAdvance, usesInitialAdvance }) => {
    const formattedChosenAdvance = +chosenAdvance;
    const valueToCheck = usesInitialAdvance ? +chosenInitialAdvance : +replaceComma(formattedChosenAdvance.toString());

    return valueToCheck < 0;
};
